import { AxiosError } from 'axios';
import { reduce } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useQuery } from 'react-query';

import { ordersApi } from 'api';
import {
  OrderCallbackUrlStatus,
  OrderStatus,
  QueryKey,
  StatusCode,
} from 'enums';
import { Order, PaymentFormValues } from 'types';
import { eventUtils } from 'utils';

const COMPLETED_ORDER_STATUSES = [OrderStatus.Cancelled, OrderStatus.Completed];

const CUSTOMER_FIELDS: Array<keyof PaymentFormValues> = [
  'customerCardFirstDigits',
  'customerCardLastDigits',
  'customerName',
  'customerBank',
  'customerPhoneLastDigits',
  'customerUtr',
  'customerIBAN',
  'customerAccountNumber',
];

export type ApiActionOptions = {
  onSuccess: (data: Order) => void;
  onError: (error: AxiosError) => void;
};

type UseOrderDataInput = {
  orderId: string;
  authenticated: boolean;
  setAuthenticationFailed: (val: boolean) => void;
  setPaymentFormValues: Dispatch<SetStateAction<PaymentFormValues>>;
};

export type UseOrderDataResult = {
  order: Order | undefined;
  setOrder: Dispatch<SetStateAction<Order | undefined>>;
  initialOrderFetched: boolean;
  isOrderLoading: boolean;
  refetchOrder: () => void;
  refetchOrderStatus: () => void;
  apiActionOptions: ApiActionOptions;
};

export const useOrderData = ({
  orderId,
  authenticated,
  setAuthenticationFailed,
  setPaymentFormValues,
}: UseOrderDataInput): UseOrderDataResult => {
  const [order, setOrder] = useState<Order>();
  const [initialOrderFetched, setInitialOrderFetched] = useState(false);
  const [timeoutRefetch, setTimeoutRefetch] = useState(false);

  const status = useMemo(() => order?.status as OrderStatus, [order]);

  const isCallbackUrlStatusInProgress = useMemo(
    () =>
      order?.integration?.callbackUrlStatus ===
      OrderCallbackUrlStatus.InProgress,
    [order],
  );

  const shouldRefetch = useMemo(() => {
    if ([OrderStatus.Requisites].includes(status)) {
      return !order?.requisites;
    }
    if (
      [
        OrderStatus.CustomerConfirm,
        OrderStatus.WaitingConfirmation,
        OrderStatus.TraderConfirm,
        OrderStatus.HoldCompleted,
        OrderStatus.Dispute,
      ].includes(status)
    ) {
      return true;
    }
    if (COMPLETED_ORDER_STATUSES.includes(status)) {
      return isCallbackUrlStatusInProgress;
    }
    return false;
  }, [isCallbackUrlStatusInProgress, order?.requisites, status]);

  const apiActionOptions = useMemo<ApiActionOptions>(
    () => ({
      onSuccess: (data: Order) => {
        setOrder(data);
        setPaymentFormValues((prev) =>
          reduce(
            CUSTOMER_FIELDS,
            (result, field) => {
              result[field] = prev[field] || data?.payment?.[field];
              return result;
            },
            { ...prev },
          ),
        );
      },
      onError: (error: AxiosError) => {
        if (error?.response?.status === StatusCode.Unauthorized) {
          setAuthenticationFailed(true);
        } else if (initialOrderFetched) {
          // Re-enables the initial-fetch query below to refetch the order. Skipped while that fetch
          // is already running: the polling and timeout queries share its key, so react-query hands
          // them its error too, and flipping the flag back to false after its onSettled set it to
          // true would leave the Reconciling effect in useOrderActions waiting forever.
          setInitialOrderFetched(false);
        }
      },
    }),
    [initialOrderFetched, setAuthenticationFailed, setPaymentFormValues],
  );

  const { isLoading: isOrderLoading } = useQuery(
    [QueryKey.Order],
    () => ordersApi.getOne(orderId),
    {
      enabled: authenticated && !initialOrderFetched,
      onSuccess: setOrder,
      onError: (error: AxiosError<{ message: string }>) => {
        // A 401 always means the session is bad, and a 403/404 that the order is no longer reachable.
        // Any other failure on the very first load (500, network) leaves no order to render either,
        // so it must land on NotFoundPage too. Only a transient failure (network, 5xx) once an order
        // is already loaded (e.g. on the compensating refetch after a cascade timeout) keeps it:
        // onSettled below still flips initialOrderFetched, and the stale order lets the Reconciling
        // effect in useOrderActions resolve it (auto-retry or the trade-method picker).
        const errorStatus = error?.response?.status;
        if (
          !order ||
          errorStatus === StatusCode.Unauthorized ||
          errorStatus === StatusCode.Forbidden ||
          errorStatus === StatusCode.NotFound
        ) {
          setAuthenticationFailed(true);
        }
        eventUtils.trackException({
          message: `Fetch order failed. Status: ${error.response?.status}. ${
            error.response?.data?.message || error.message
          }`,
        });
      },
      onSettled: () => setInitialOrderFetched(true),
    },
  );

  const queryResultOrder = useQuery(
    QueryKey.Order,
    () => ordersApi.getOne(orderId),
    {
      enabled: shouldRefetch,
      refetchInterval: 3000,
      ...apiActionOptions,
    },
  );

  useQuery(QueryKey.Order, () => ordersApi.getOne(orderId), {
    enabled: timeoutRefetch,
    ...apiActionOptions,
  });

  const refetchOrder = useCallback(
    () => queryResultOrder.refetch(),
    [queryResultOrder],
  );
  const refetchOrderStatus = useCallback(() => setTimeoutRefetch(true), []);

  return {
    order,
    setOrder,
    initialOrderFetched,
    isOrderLoading,
    refetchOrder,
    refetchOrderStatus,
    apiActionOptions,
  };
};
